import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import PreviewTable from "./PreviewTable";
import { API, MappingEditor, PhaseLockControl, Stat, Section, FileUpload } from "../App";
const PHASE_LABELS = {
  phase1: "Phase 1 - Master Sanitation",
  phase2: "Phase 2 - Master vs Tax",
  phase3: "Phase 3 - OTA Reconciliation",
  phase4: "Phase 4 - Razorpay Reconciliation",
  phase5: "Phase 5 - Final Verified Audit",
};

function Phase4({ session, refresh }) {
  const [busy, setBusy] = useState(false);
  const result = session.phase_results?.phase4;
  const phase4Ok = !!(result && result.ok);
  const locked = session.locked_phases?.phase4?.locked;

  const p4Actions = [
    { label: "✓ Mark Resolved", type: "resolved", className: "h-8 text-green-600 border-green-200 hover:bg-green-50" },
    { label: "✕ Ignore Error", type: "ignored", className: "h-8 text-slate-600 border-slate-200 hover:bg-slate-50" }
  ];

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/audit/session/${session.id}/phase4`);
      if (res.data && res.data.ok === false) throw new Error(res.data.error || "Phase 4 backend error");
      toast.success("Phase 4 (Razorpay) complete!");
      refresh();
    } catch (e) {
      toast.error(`Phase 4 failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

    const handlePhase4Override = async (phase, identifiers, overrideType = "ignored") => {
    try {
      await axios.post(`${API}/audit/session/${session.id}/override`, {
        phase: "phase4",
        identifiers: identifiers,
        resolved: overrideType === "resolved",
        override_type: overrideType
      });
      toast.success(`Successfully marked ${identifiers.length} item(s) as ${overrideType}`);
      refresh();
    } catch (e) {
      toast.error(`Failed to apply override: ${e.response?.data?.detail || e.message}`);
    }
  };

  const hasFiles = session.files?.master && session.files?.payment_gateway && session.files?.razorpay_raw;

  const getActiveCount = (rows) => {
    if (!rows) return 0;
    const phaseOverrides = session.overrides?.phase4 || [];
    return rows.filter(r => {
      const id = r.booking_id || r.ota_booking_id || (r.ota_pnrs && r.ota_pnrs[0]);
      if (!id) return true;
      return !phaseOverrides.includes(String(id));
    }).length;
  };

  const activeTampering = getActiveCount(result?.tampering_alerts);
  const activeMissingRz = getActiveCount(result?.missing_in_razorpay);
  const activeMissingMaster = getActiveCount(result?.missing_in_master);
  const activeMissingGw = getActiveCount(result?.missing_in_gateway);
  const activeSfOvercharge = getActiveCount(result?.stayflexi_overcharges);
  const activePgOvercharge = getActiveCount(result?.pg_overcharges);
  const activeLinkViolation = getActiveCount(result?.payment_link_violations);

  return (
    <Card className="shadow-sm border-blue-100 dark:border-slate-800">
      <CardHeader className="bg-slate-50/50 dark:bg-slate-900/50 border-b pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-blue-900 dark:text-blue-100">
              {PHASE_LABELS.phase4}
            </CardTitle>
            <CardDescription className="mt-1.5">
              Left-joins Stayflexi Gateway Report with Raw Razorpay Dashboard to audit payment fees.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {phase4Ok ? (
              <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100 px-3 py-1">
                Completed
              </Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500 px-3 py-1">
                Pending
              </Badge>
            )}
            <Button
              onClick={run}
              disabled={busy || !hasFiles || locked}
              size="sm"
              className="min-w-[100px]"
            >
              {busy ? "Running..." : phase4Ok ? "Re-run Phase 4" : "Run Phase 4"}
            </Button>
          </div>
        </div>
        {!hasFiles && (
          <p className="text-xs text-amber-600 mt-2 font-medium">
            Requires Master, Stayflexi Gateway, and Raw Razorpay files to be uploaded.
          </p>
        )}
      </CardHeader>
      
      {phase4Ok && result && (
        <CardContent className="pt-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg border">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">Master Bookings Processed</p>
              <p className="text-2xl font-bold">{result.total_razorpay_rows}</p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg border">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">Gateway Matches Found</p>
              <p className="text-2xl font-bold">{result.unique_bookings}</p>
            </div>
            <div className={`p-4 rounded-lg border ${activeTampering > 0 ? "bg-red-50 border-red-200" : "bg-slate-50"}`}>
              <p className={`text-xs uppercase tracking-wider font-semibold mb-1 ${activeTampering > 0 ? "text-red-700" : "text-muted-foreground"}`}>Tampering Alerts</p>
              <p className={`text-2xl font-bold ${activeTampering > 0 ? "text-red-700" : ""}`}>{activeTampering}</p>
            </div>
            <div className={`p-4 rounded-lg border ${activeSfOvercharge > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50"}`}>
              <p className={`text-xs uppercase tracking-wider font-semibold mb-1 ${activeSfOvercharge > 0 ? "text-amber-700" : "text-muted-foreground"}`}>Stayflexi Overcharges</p>
              <p className={`text-2xl font-bold ${activeSfOvercharge > 0 ? "text-amber-700" : ""}`}>{activeSfOvercharge}</p>
            </div>
          </div>
          
          <Tabs defaultValue="tampering" className="w-full">
            <TabsList className="mb-4 flex-wrap gap-2">
              <TabsTrigger value="tampering">Tampering ({activeTampering})</TabsTrigger>
              <TabsTrigger value="missing_rz">Missing in Razorpay ({activeMissingRz})</TabsTrigger>
              <TabsTrigger value="missing_gw">Missing in Gateway ({activeMissingGw})</TabsTrigger>
              <TabsTrigger value="missing_master">Unaccounted Revenue ({activeMissingMaster})</TabsTrigger>
              <TabsTrigger value="sf_overcharge">Stayflexi Overcharge ({activeSfOvercharge})</TabsTrigger>
              <TabsTrigger value="pg_overcharge">PG Limit ({activePgOvercharge})</TabsTrigger>
              <TabsTrigger value="link_violation">Link Violations ({activeLinkViolation})</TabsTrigger>
              <TabsTrigger value="matches">Gateway Matches ({result.matches_sample?.length || 0})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="tampering">
               <PreviewTable session={session} rows={result.tampering_alerts} phaseKey="phase4" onOverride={handlePhase4Override} overrideActions={p4Actions} />
            </TabsContent>
            <TabsContent value="missing_rz">
               <PreviewTable session={session} rows={result.missing_in_razorpay} phaseKey="phase4" onOverride={handlePhase4Override} overrideActions={p4Actions} />
            </TabsContent>
            <TabsContent value="missing_gw">
               <PreviewTable session={session} rows={result.missing_in_gateway} phaseKey="phase4" onOverride={handlePhase4Override} overrideActions={p4Actions} />
            </TabsContent>
            <TabsContent value="missing_master">
               <PreviewTable session={session} rows={result.missing_in_master} phaseKey="phase4" onOverride={handlePhase4Override} overrideActions={p4Actions} />
            </TabsContent>
            <TabsContent value="sf_overcharge">
               <PreviewTable session={session} rows={result.stayflexi_overcharges} phaseKey="phase4" onOverride={handlePhase4Override} overrideActions={p4Actions} />
            </TabsContent>
            <TabsContent value="pg_overcharge">
               <PreviewTable session={session} rows={result.pg_overcharges} phaseKey="phase4" onOverride={handlePhase4Override} overrideActions={p4Actions} />
            </TabsContent>
            <TabsContent value="link_violation">
               <PreviewTable session={session} rows={result.payment_link_violations} phaseKey="phase4" onOverride={handlePhase4Override} overrideActions={p4Actions} />
            </TabsContent>
            <TabsContent value="matches">
               <PreviewTable session={session} rows={result.matches_sample} phaseKey="phase4" />
            </TabsContent>
          </Tabs>
        </CardContent>
      )}
    </Card>
  );
}


export default Phase4;
